export interface PageSizeInput {
  containerHeight: number;
  rowHeight: number;
  gap: number;
  min: number;
  max: number;
  fallback: number;
}

/** Number of whole rows (with gaps between them) that fit into the container. */
export function computePageSize({
  containerHeight,
  rowHeight,
  gap,
  min,
  max,
  fallback,
}: PageSizeInput): number {
  if (
    !Number.isFinite(containerHeight) ||
    !Number.isFinite(rowHeight) ||
    containerHeight <= 0 ||
    rowHeight <= 0
  ) {
    return fallback;
  }
  const safeGap = Number.isFinite(gap) && gap > 0 ? gap : 0;
  const fit = Math.floor((containerHeight + safeGap) / (rowHeight + safeGap));
  return Math.min(max, Math.max(min, fit));
}

export interface Debounced {
  (): void;
  cancel: () => void;
}

/** Trailing debounce: `fn` runs once, `delay` ms after the last call. */
export function createDebounced(fn: () => void, delay: number): Debounced {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const debounced = (() => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      fn();
    }, delay);
  }) as Debounced;
  debounced.cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  return debounced;
}
